import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { Building, CheckCircle, TrendingUp, Clock, FileText, Handshake, DollarSign, ArrowRight, Phone, Users } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from "@/components/ui/accordion";
import CTABanner from "@/components/CTABanner";
import SEO from "@/components/SEO";

export default function InspectionsForSellers() {
  const sellerBenefits = [
    {
      icon: TrendingUp,
      title: "Faster Sales",
      description: "Properties with pre-sale inspections often sell faster. Buyers have confidence and there are fewer surprises to slow down the process."
    },
    {
      icon: Handshake,
      title: "Reduce Renegotiations",
      description: "When buyers know the condition upfront, there's less room for last-minute price negotiations based on inspection findings."
    },
    {
      icon: DollarSign,
      title: "Protect Your Price",
      description: "Address issues before listing or factor them into your asking price. No nasty surprises that could derail your sale."
    },
    {
      icon: Users,
      title: "Build Buyer Trust",
      description: "Providing an inspection report shows transparency and builds trust with potential buyers from the start."
    }
  ];

  const howItWorks = [
    {
      step: "1",
      title: "Book Your Inspection",
      description: "Contact us to arrange a convenient inspection time, ideally before you list the property."
    },
    {
      step: "2",
      title: "We Inspect the Property",
      description: "Our experienced inspector conducts a thorough visual assessment of all accessible areas."
    },
    {
      step: "3",
      title: "Receive Your Report",
      description: "Within 72 hours, you'll receive a clear, professional report with photos documenting the property's condition."
    },
    {
      step: "4",
      title: "List with Confidence",
      description: "Use the report to address issues, inform your agent, or share with buyers to demonstrate transparency."
    }
  ];

  const faqs = [
    {
      question: "Why should I get an inspection before selling?",
      answer: "A pre-sale inspection helps you understand your property's condition before buyers do. You can address issues, adjust your price accordingly, or simply demonstrate transparency to buyers. This often leads to faster sales and fewer renegotiations."
    },
    {
      question: "Won't buyers want their own inspection anyway?",
      answer: "Many buyers will still get their own inspection, but having a pre-sale report shows good faith and can speed up negotiations. Some buyers may rely on your report, especially in competitive markets."
    },
    {
      question: "What if the inspection finds problems?",
      answer: "Finding issues before listing gives you options: fix them, adjust your price, or disclose them upfront. It's much better to know about problems before a buyer's inspector finds them and uses them to negotiate."
    },
    {
      question: "How recent should a pre-sale inspection be?",
      answer: "Ideally, complete the inspection within a few weeks of listing. Reports older than 3 months may be questioned by buyers as conditions can change."
    },
    {
      question: "Should I share the report with buyers?",
      answer: "That's your choice. Many sellers make the report available to serious buyers as a sign of transparency. Your real estate agent can advise on the best approach for your market."
    },
    {
      question: "Do you offer inspections for agents?",
      answer: "Absolutely! We work closely with real estate agents across Darwin and the NT. We offer fast turnaround, professional service, and can work directly with your agents on scheduling and delivery."
    }
  ];

  const services = [
    { name: "Pre-Sale Property Inspections", path: "/services/pre-sale", description: "Our most popular service for sellers — comprehensive visual assessment" },
    { name: "Building Condition Reports", path: "/services/building-condition", description: "Detailed condition report with remediation estimates included" },
    { name: "Pool Reports", path: "/services/pool", description: "Essential if your property has a pool — compliance guidance included" },
  ];

  const sellerSchema = {
    "@context": "https://schema.org",
    "@type": "Service",
    "name": "Pre-Sale Building Inspections for Home Sellers",
    "description": "Professional pre-sale building inspections helping Darwin home sellers prepare their property for market. Reduce negotiations and sell faster.",
    "provider": {
      "@type": "LocalBusiness",
      "name": "Aussie Inspections",
      "telephone": "+61413245519",
      "address": {
        "@type": "PostalAddress",
        "addressLocality": "Darwin",
        "addressRegion": "NT",
        "addressCountry": "AU"
      }
    },
    "areaServed": {
      "@type": "City",
      "name": "Darwin"
    },
    "offers": {
      "@type": "Offer",
      "price": "260",
      "priceCurrency": "AUD"
    }
  };

  return (
    <div>
      <SEO 
        title="Inspections for Home Sellers"
        description="Pre-sale building inspections for Darwin home sellers. Sell faster, reduce negotiations, and demonstrate transparency to buyers. Pre-sale inspections from $325 incl. GST."
        keywords="pre-sale inspection Darwin, selling house inspection, vendor inspection, property inspection before selling, pre-listing inspection Darwin"
        url="/inspections-for-sellers"
        schema={sellerSchema}
      />

      {/* CTA Banner */}
      <CTABanner />

      {/* Hero Section */}
      <section className="relative h-[50vh] min-h-[400px] flex items-center bg-slate-900">
        <div className="absolute inset-0">
          <img
            src="/assets/img/house-for-sale.jpg"
            alt="House for sale"
            className="w-full h-full object-cover opacity-30"
          />
        </div>

        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 w-full">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            className="max-w-3xl"
          >
            <div className="flex items-center space-x-2 text-secondary mb-4">
              <Building className="w-5 h-5" />
              <span className="font-semibold uppercase tracking-wider text-sm">For Home Sellers</span>
            </div>
            <h1 className="font-chivo text-4xl sm:text-5xl lg:text-6xl font-black tracking-tight leading-none text-white mb-4">
              Sell Smarter, Sell Faster
            </h1>
            <p className="text-lg text-slate-200">
              Know your property&apos;s condition before buyers do. Pre-sale inspections help you sell with confidence and reduce last-minute surprises.
            </p>
          </motion.div>
        </div>
      </section>

      {/* Benefits Section */}
      <section className="py-16 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.2 }}
            className="text-center mb-12"
          >
            <h2 className="font-chivo text-3xl font-bold text-slate-900 mb-4">
              Why Sellers Choose Pre-Sale Inspections
            </h2>
            <p className="text-lg text-slate-600 max-w-2xl mx-auto">
              Smart sellers know that knowledge is power. Understanding your property&apos;s condition puts you in control.
            </p>
          </motion.div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
            {sellerBenefits.map((benefit, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.1 * index }}
                className="text-center"
              >
                <div className="w-16 h-16 rounded-full bg-secondary/10 flex items-center justify-center mx-auto mb-4">
                  <benefit.icon className="w-8 h-8 text-secondary" />
                </div>
                <h3 className="font-chivo text-lg font-bold text-slate-900 mb-2">
                  {benefit.title}
                </h3>
                <p className="text-slate-600 text-sm">
                  {benefit.description}
                </p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* How It Works */}
      <section className="py-16 bg-slate-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="font-chivo text-3xl font-bold text-slate-900 mb-12 text-center">
            How It Works
          </h2>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
            {howItWorks.map((item, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.1 * index }}
                className="relative"
              >
                <div className="bg-white p-6 rounded-lg border border-slate-200 h-full">
                  <div className="w-10 h-10 rounded-full bg-primary text-white flex items-center justify-center font-bold mb-4">
                    {item.step}
                  </div>
                  <h3 className="font-chivo text-lg font-bold text-slate-900 mb-2">
                    {item.title}
                  </h3>
                  <p className="text-slate-600 text-sm">
                    {item.description}
                  </p>
                </div>
                {index < howItWorks.length - 1 && (
                  <div className="hidden lg:block absolute top-1/2 -right-4 transform -translate-y-1/2">
                    <ArrowRight className="w-6 h-6 text-slate-300" />
                  </div>
                )}
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Stats Section */}
      <section className="py-12 bg-primary">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8 text-center text-white">
            <div>
              <div className="text-4xl font-bold mb-2">72hr</div>
              <div className="text-blue-100">Report Delivery</div>
            </div>
            <div>
              <div className="text-4xl font-bold mb-2">$325</div>
              <div className="text-blue-100">From (incl. GST)</div>
            </div>
            <div>
              <div className="text-4xl font-bold mb-2">100%</div>
              <div className="text-blue-100">Satisfaction</div>
            </div>
          </div>
        </div>
      </section>

      {/* For Agents Section */}
      <section className="py-16 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
            <div>
              <div className="inline-block bg-secondary/10 text-secondary px-3 py-1 rounded-full text-sm font-semibold mb-4">
                For Real Estate Agents
              </div>
              <h2 className="font-chivo text-3xl font-bold text-slate-900 mb-6">
                Trusted by Agents Across the NT
              </h2>
              <p className="text-slate-600 mb-6">
                We work closely with real estate agents to deliver fast, professional inspection services. Our reports help you close deals faster and reduce the risk of sales falling through.
              </p>
              <ul className="space-y-3 mb-8">
                <li className="flex items-start space-x-3">
                  <CheckCircle className="w-5 h-5 text-secondary flex-shrink-0 mt-0.5" />
                  <span className="text-slate-700">Fast 72-hour turnaround</span>
                </li>
                <li className="flex items-start space-x-3">
                  <CheckCircle className="w-5 h-5 text-secondary flex-shrink-0 mt-0.5" />
                  <span className="text-slate-700">Professional, clear reports your clients understand</span>
                </li>
                <li className="flex items-start space-x-3">
                  <CheckCircle className="w-5 h-5 text-secondary flex-shrink-0 mt-0.5" />
                  <span className="text-slate-700">Flexible scheduling to suit property access</span>
                </li>
                <li className="flex items-start space-x-3">
                  <CheckCircle className="w-5 h-5 text-secondary flex-shrink-0 mt-0.5" />
                  <span className="text-slate-700">Direct communication with your preferred inspector</span>
                </li>
              </ul>
              <Button
                asChild
                className="bg-secondary hover:bg-secondary/90 text-white font-bold tracking-wide uppercase"
              >
                <Link to="/contact">Partner with Us</Link>
              </Button>
            </div>
            <div className="relative">
              <img
                src="/assets/img/agent-and-client.jpg"
                alt="Real estate agent and client"
                className="rounded-lg shadow-xl"
              />
            </div>
          </div>
        </div>
      </section>

      {/* Services for Sellers */}
      <section className="py-16 bg-slate-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="font-chivo text-3xl font-bold text-slate-900 mb-4 text-center">
            Inspection Services for Sellers
          </h2>
          <p className="text-lg text-slate-600 max-w-2xl mx-auto text-center mb-12">
            Choose the right inspection for your property. All reports delivered within 72 hours.
          </p>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            {services.map((service, index) => (
              <Link
                key={index}
                to={service.path}
                className="group bg-white p-6 rounded-lg border border-slate-200 hover:border-primary hover:shadow-lg transition-all"
              >
                <div className="flex items-start justify-between">
                  <div>
                    <h3 className="font-chivo text-lg font-bold text-slate-900 group-hover:text-primary mb-2">
                      {service.name}
                    </h3>
                    <p className="text-slate-600 text-sm">{service.description}</p>
                  </div>
                  <ArrowRight className="w-5 h-5 text-slate-400 group-hover:text-primary group-hover:translate-x-1 transition-all flex-shrink-0 ml-4" />
                </div>
              </Link>
            ))}
          </div>
        </div>
      </section>

      {/* FAQs */}
      <section className="py-16 bg-white">
        <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="font-chivo text-3xl font-bold text-slate-900 mb-8 text-center">
            Frequently Asked Questions
          </h2>
          <Accordion type="single" collapsible className="w-full">
            {faqs.map((faq, index) => (
              <AccordionItem key={index} value={`item-${index}`}>
                <AccordionTrigger className="text-left font-semibold text-slate-900">
                  {faq.question}
                </AccordionTrigger>
                <AccordionContent className="text-slate-600">
                  {faq.answer}
                </AccordionContent>
              </AccordionItem>
            ))}
          </Accordion>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-16 bg-secondary">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="font-chivo text-3xl sm:text-4xl font-bold text-white mb-4">
            Ready to Prepare Your Property for Sale?
          </h2>
          <p className="text-lg text-orange-100 mb-2">
            Book your pre-sale inspection today
          </p>
          <p className="text-orange-100 mb-8">
            From $325 incl. GST • 72-hour report delivery • Flat-rate pricing
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Button
              asChild
              size="lg"
              className="bg-white hover:bg-slate-100 text-secondary font-bold tracking-wide uppercase px-12"
            >
              <Link to="/contact">Book Inspection</Link>
            </Button>
            <Button
              asChild
              variant="outline"
              size="lg"
              className="border-2 border-white/30 hover:border-white hover:bg-white/10 text-white font-bold tracking-wide uppercase px-12"
            >
              <a href="tel:+61413245519" className="flex items-center space-x-2">
                <Phone className="w-4 h-4" />
                <span>0413 24 55 19</span>
              </a>
            </Button>
          </div>
        </div>
      </section>
    </div>
  );
}
